// @core/stores/auth.ts
// Auto-generated by corebuilder. DO NOT EDIT.

import { makeAutoObservable, runInAction } from 'mobx';
import { initializeApiClient, type AuthTokens } from '../api/client';
import type { SignInRequest } from '../types/authn/SignInRequest';
import type { SignInResponse } from '../types/authn/SignInResponse';
import type { SignUpRequest } from '../types/authn/SignUpRequest';
import type { SignUpResponse } from '../types/authn/SignUpResponse';
import type { User } from '../types/authn/User';
import { SignIn } from '../api/endpoints/authn/sign_in';
import { SignUp } from '../api/endpoints/authn/sign_up';
import { InvalidateCurrentTokens } from '../api/endpoints/authn/invalidate_current_tokens';
import { ListUserprofilesForService } from '../api/endpoints/userprofile/list_userprofiles_for_service';

// ============================================
// Storage Keys
// ============================================

const STORAGE_KEYS = {
  ACCESS_TOKEN: 'auth_access_token',
  REFRESH_TOKEN: 'auth_refresh_token',
  USER: 'auth_user',
  USER_ID: 'auth_user_id',
  ACTIVE_PROFILE_ID: 'auth_active_profile_id',
} as const;

// ============================================
// Auth Store
// ============================================

class AuthStore {
  // State
  user: User | null = null;
  userId: string | null = null;
  activeProfileId: string | null = null;
  accessToken: string | null = null;
  refreshToken: string | null = null;
  isLoading = false;
  isInitialized = false;
  error: unknown = null;
  requiresTwoFactor = false;
  pendingCredentials: SignInRequest | null = null;

  // API configuration
  private apiBaseUrl = '';

  constructor() {
    makeAutoObservable(this, {
      pendingCredentials: false,
    });
  }

  // ============================================
  // Computed Properties
  // ============================================

  get isAuthenticated(): boolean {
    return !!this.accessToken && (!!this.user || !!this.userId);
  }

  get hasRefreshToken(): boolean {
    return !!this.refreshToken;
  }

  // ============================================
  // Initialization
  // ============================================

  /**
   * Initialize auth store and API client
   * Call this once at app startup
   */
  initialize(config: {
    apiBaseUrl: string;
    scope?: { platformId?: string; productId?: string; tenantId?: string };
  }): void {
    this.apiBaseUrl = config.apiBaseUrl;

    // Load saved state from storage
    this.loadFromStorage();

    // Initialize API client with auth integration
    initializeApiClient({
      baseUrl: this.apiBaseUrl,
      scope: config.scope,
      getAccessToken: () => this.accessToken,
      getRefreshToken: () => this.refreshToken,
      getActiveProfileId: () => this.activeProfileId,
      refreshTokens: this.refreshTokens.bind(this),
      onTokenRefreshed: this.handleTokenRefreshed.bind(this),
      onUnauthorized: this.handleUnauthorized.bind(this),
    });

    runInAction(() => {
      this.isInitialized = true;
    });
  }

  // ============================================
  // Storage Management
  // ============================================

  private loadFromStorage(): void {
    if (typeof window === 'undefined') return;

    try {
      const accessToken = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
      const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
      const userJson = localStorage.getItem(STORAGE_KEYS.USER);
      const userId = localStorage.getItem(STORAGE_KEYS.USER_ID);

      if (accessToken) {
        this.accessToken = accessToken;
      }
      if (refreshToken) {
        this.refreshToken = refreshToken;
      }
      if (userJson) {
        this.user = JSON.parse(userJson);
      }
      if (userId) {
        this.userId = userId;
      }

      const activeProfileId = localStorage.getItem(STORAGE_KEYS.ACTIVE_PROFILE_ID);
      if (activeProfileId) {
        this.activeProfileId = activeProfileId;
      }
    } catch (error) {
      console.error('Failed to load auth state from storage:', error);
      this.clearStorage();
    }
  }

  private saveToStorage(): void {
    if (typeof window === 'undefined') return;

    try {
      if (this.accessToken) {
        localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, this.accessToken);
      } else {
        localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
      }

      if (this.refreshToken) {
        localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, this.refreshToken);
      } else {
        localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
      }

      if (this.user) {
        localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(this.user));
      } else {
        localStorage.removeItem(STORAGE_KEYS.USER);
      }

      if (this.userId) {
        localStorage.setItem(STORAGE_KEYS.USER_ID, this.userId);
      } else {
        localStorage.removeItem(STORAGE_KEYS.USER_ID);
      }

      if (this.activeProfileId) {
        localStorage.setItem(STORAGE_KEYS.ACTIVE_PROFILE_ID, this.activeProfileId);
      } else {
        localStorage.removeItem(STORAGE_KEYS.ACTIVE_PROFILE_ID);
      }
    } catch (error) {
      console.error('Failed to save auth state to storage:', error);
    }
  }

  private clearStorage(): void {
    if (typeof window === 'undefined') return;

    localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.USER);
    localStorage.removeItem(STORAGE_KEYS.USER_ID);
    localStorage.removeItem(STORAGE_KEYS.ACTIVE_PROFILE_ID);
  }

  // ============================================
  // Token Management
  // ============================================

  private async refreshTokens(refreshToken: string): Promise<AuthTokens> {
    // Raw fetch to avoid circular dependency with API client interceptors
    const response = await fetch(`${this.apiBaseUrl}/authn/auth/tokens/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });

    if (!response.ok) {
      throw new Error('Token refresh failed');
    }

    const data: SignInResponse = await response.json();
    return {
      accessToken: data.access_token,
      refreshToken: data.refresh_token,
    };
  }

  private handleTokenRefreshed(tokens: AuthTokens): void {
    runInAction(() => {
      this.accessToken = tokens.accessToken;
      if (tokens.refreshToken) {
        this.refreshToken = tokens.refreshToken;
      }
      this.saveToStorage();
    });
  }

  private handleUnauthorized(): void {
    this.logout();
  }

  // ============================================
  // Authentication Actions
  // ============================================

  /**
   * Login with credentials
   */
  async login(credentials: SignInRequest): Promise<SignInResponse> {
    runInAction(() => {
      this.isLoading = true;
      this.error = null;
      this.requiresTwoFactor = false;
    });

    try {
      const data = await SignIn(credentials);

      // Handle 2FA requirement
      if (data.two_factor_required) {
        runInAction(() => {
          this.requiresTwoFactor = true;
          this.pendingCredentials = credentials;
          this.isLoading = false;
        });
        return data;
      }

      // Successful login — set tokens first
      const userId = data.user_id;
      runInAction(() => {
        this.userId = userId;
        this.accessToken = data.access_token;
        this.refreshToken = data.refresh_token ?? null;
        this.requiresTwoFactor = false;
        this.pendingCredentials = null;
        this.saveToStorage();
      });

      // Resolve active profile before completing login
      await this.resolveDefaultProfile(data.profile_id, userId);

      runInAction(() => {
        this.isLoading = false;
      });

      return data;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Complete login with 2FA code
   */
  async loginWithTwoFactor(code: string): Promise<SignInResponse> {
    if (!this.pendingCredentials) {
      throw new Error('No pending login. Call login() first.');
    }

    return this.login({
      ...this.pendingCredentials,
      two_factor_code: code,
    });
  }

  /**
   * Register a new user
   */
  async register(data: SignUpRequest): Promise<SignUpResponse> {
    runInAction(() => {
      this.isLoading = true;
      this.error = null;
    });

    try {
      const responseData = await SignUp(data);
      // Registration does NOT auto-login. User is redirected to /login afterwards;
      // profile is created asynchronously by userprofile service via Kafka.
      runInAction(() => {
        this.isLoading = false;
      });
      return responseData;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Logout and clear all auth state
   */
  async logout(): Promise<void> {
    // Try to invalidate tokens on server (fire and forget)
    if (this.accessToken && this.refreshToken) {
      InvalidateCurrentTokens({ refresh_token: this.refreshToken }).catch(() => {
        // Ignore errors - we're logging out anyway
      });
    }

    runInAction(() => {
      this.user = null;
      this.userId = null;
      this.activeProfileId = null;
      this.accessToken = null;
      this.refreshToken = null;
      this.error = null;
      this.requiresTwoFactor = false;
      this.pendingCredentials = null;
      this.clearStorage();
    });
  }

  // ============================================
  // State Setters (for external use)
  // ============================================

  /**
   * Manually set credentials (e.g., from OAuth callback)
   */
  setCredentials(response: SignInResponse): void {
    runInAction(() => {
      this.userId = response.user_id;
      this.accessToken = response.access_token;
      this.refreshToken = response.refresh_token ?? null;
      this.error = null;
      this.saveToStorage();
    });
  }

  /**
   * Update user data
   */
  setUser(user: User): void {
    runInAction(() => {
      this.user = user;
      this.saveToStorage();
    });
  }

  /**
   * Clear error state
   */
  clearError(): void {
    runInAction(() => {
      this.error = null;
    });
  }

  /**
   * Cancel 2FA flow
   */
  cancelTwoFactor(): void {
    runInAction(() => {
      this.requiresTwoFactor = false;
      this.pendingCredentials = null;
    });
  }

  /**
   * Resolve default profile after login.
   * Priority: profile_id from sign-in response → is_default profile → first profile.
   */
  private async resolveDefaultProfile(profileIdFromResponse?: string, userId?: string): Promise<void> {
    // 1. Use profile_id from sign-in response if available
    if (profileIdFromResponse) {
      runInAction(() => {
        this.activeProfileId = profileIdFromResponse;
        this.saveToStorage();
      });
      return;
    }

    // 2. Fetch user's profiles and pick the default one
    if (!userId) return;
    try {
      const resp = await ListUserprofilesForService(userId);
      const profiles = resp.userprofiles?.profiles ?? [];
      const defaultProfile = profiles.find(p => p.is_default) ?? profiles[0];
      if (defaultProfile?.entity?.id) {
        runInAction(() => {
          this.activeProfileId = defaultProfile.entity!.id;
          this.saveToStorage();
        });
      }
    } catch (err) {
      console.error('Failed to resolve default profile:', err);
    }
  }

  /**
   * Switch active profile
   * Sets the profile ID that will be sent as X-Active-Profile header
   */
  switchProfile(profileId: string): void {
    runInAction(() => {
      this.activeProfileId = profileId;
      this.saveToStorage();
    });
  }
}

// Singleton instance
export const authStore = new AuthStore();
